/**
 * "Add swatch" on Fabric Shoppe product cards.
 *
 * The form posts to /cart/add without JavaScript. With JavaScript we add the
 * swatch with the AJAX Cart API, ask for the cart sections to be re-rendered
 * (Section Rendering API) and announce the change with Shopify's standard
 * CartLinesUpdateEvent. Horizon's cart icon and cart drawer listen for that
 * event, so the count and drawer update without a page load.
 *
 * One delegated listener covers every card, including cards added later by
 * "Load more" or recommendations.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_RESET_MS = 4000;

/** @param {HTMLFormElement} form */
function setState(form, state) {
  const button = form.querySelector('button[type="submit"]');
  if (!button) return;
  const defaultLabel = button.querySelector('[data-default-label]');
  const addedLabel = button.querySelector('[data-added-label]');

  button.removeAttribute('aria-busy');
  button.dataset.state = state;

  if (state === 'busy') {
    button.setAttribute('aria-busy', 'true');
  }
  if (defaultLabel && addedLabel) {
    defaultLabel.hidden = state === 'added';
    addedLabel.hidden = state !== 'added';
  }
}

/** @param {HTMLFormElement} form @param {string} message */
function setStatus(form, message, isError = false) {
  const button = form.querySelector('button[type="submit"]');
  const status = button && document.getElementById(button.getAttribute('aria-describedby') || '');
  if (!status) return;
  status.textContent = message;
  status.classList.toggle('fs-card__status--error', isError);
}

async function getCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form || !form.hasAttribute('data-fs-swatch-form')) return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  const variantId = Number(new FormData(form).get('id'));
  const title = form.dataset.productTitle || 'Swatch';
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);

  setState(form, 'busy');
  setStatus(form, '');

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Could not add the swatch. Please try again.';
      form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'fs-product-card', itemCount: 1 },
      });
      setState(form, 'idle');
      setStatus(form, message, true);
      return;
    }

    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'fs-product-card',
        itemCount: 1,
        sections: data.sections,
      },
    });

    setState(form, 'added');
    // Screen readers hear this; sighted users see the button change.
    setStatus(form, '');
    const live = document.querySelector('[data-testid="cart-count-live-region"], #FsCartLive');
    if (live) live.textContent = `9x9" swatch of ${title} added to your cart.`;

    window.setTimeout(() => setState(form, 'idle'), ADDED_RESET_MS);
  } catch (error) {
    deferred.reject(error);
    setState(form, 'idle');
    setStatus(form, 'Could not add the swatch. Check your connection and try again.', true);
  }
});
